import { useCallback, useEffect, useRef, useState } from 'react';
import { distanceInMeters } from '../trashcan/domain/distance';
import {
  claimLocationLoot,
  createLootRequestId,
  getLootErrorMessage,
  loadLootLog as loadLootLogFromServer,
  LootApiError,
  waitForLootClaimResolution,
} from '../api/lootApi';
import { getLootEligibilityState } from '../game-map/core.mjs';
import { LOOT_RADIUS_METERS } from '../game-map/locationPolicy.mjs';

const LOOT_COOLDOWN_MS = 30 * 60 * 1000;
const CLICK_GUARD_MS = 800;

function toLocationId(location) {
  return String(location?.id ?? location?.location_id ?? '');
}

function toSpotKey(location) {
  const explicit = String(location?.spotKey ?? location?.key ?? '');
  if (/^(wp:[1-9][0-9]*|osm:(node|way|relation):[1-9][0-9]{0,18})$/.test(explicit)) return explicit;
  const numeric = Number(location?.id ?? location?.location_id);
  return Number.isInteger(numeric) && numeric > 0 ? `wp:${numeric}` : '';
}

function parseLootTimestamp(value) {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const mysqlMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
  if (mysqlMatch) {
    const [, year, month, day, hour, minute, second] = mysqlMatch.map(Number);
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function mergeClaimIntoLootLog(current, locationId, claim) {
  const key = String(locationId || '');
  const lastLootAt = String(claim?.last_loot_at || '').trim();
  if (!key || !lastLootAt) return current;

  const nextEntry = {
    location_id: key,
    location_key: String(claim?.spot_key || ''),
    last_loot_at: lastLootAt,
  };
  const existingIndex = current.findIndex((entry) => String(entry?.location_id) === key);
  if (existingIndex < 0) return [nextEntry, ...current];

  const next = [...current];
  next[existingIndex] = { ...next[existingIndex], ...nextEntry };
  return next;
}

function getEligibilityMessage(eligibility) {
  return ({
    logged_out: 'Melde dich an, um diesen Ort zu plündern.',
    gps_off: 'Aktiviere GPS, um diesen Ort zu plündern.',
    gps_inaccurate: 'Das GPS-Signal ist zu ungenau.',
    gps_stale: 'Der GPS-Standort ist zu alt. Bitte starte GPS erneut.',
    cooldown: `Cooldown aktiv! Noch ${Math.ceil(Number(eligibility?.cooldownMs || 0) / 60000)} Min.`,
    too_far: `Du bist noch ${Math.ceil(Number(eligibility?.distance || 0))} m entfernt.`,
  })[eligibility?.reason] || 'Der Standort ist derzeit nicht verfügbar.';
}

export function useLooting({
  userPos,
  userId,
  geolocationAccuracy,
  geolocationTimestamp,
  geolocationReceivedAt,
  requestFreshPosition,
  applyGamificationPayload,
  playXpSound,
} = {}) {
  const [lootMessage, setLootMessage] = useState(null);
  const [lootLog, setLootLog] = useState([]);
  const [isLooting, setIsLooting] = useState(false);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  const clearTimerRef = useRef(null);
  const claimLockRef = useRef(false);
  const claimUnlockTimerRef = useRef(null);

  const loadLootLog = useCallback(async () => {
    if (!userId) {
      setLootLog([]);
      return [];
    }
    try {
      const normalized = await loadLootLogFromServer();
      setLootLog(normalized);
      return normalized;
    } catch (error) {
      console.warn('Lootlog konnte nicht geladen werden:', error);
      setLootLog([]);
      return [];
    }
  }, [userId]);

  useEffect(() => {
    loadLootLog();
  }, [loadLootLog]);

  useEffect(() => {
    const hasActiveCooldown = lootLog.some((entry) => {
      const lastLootAt = parseLootTimestamp(entry?.last_loot_at);
      return lastLootAt > 0 && Math.max(0, LOOT_COOLDOWN_MS - (Date.now() - lastLootAt)) > 0;
    });
    if (!hasActiveCooldown) return undefined;
    const timer = setInterval(() => setCooldownNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [lootLog]);

  useEffect(() => () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    if (claimUnlockTimerRef.current) clearTimeout(claimUnlockTimerRef.current);
  }, []);

  const getCooldownTime = useCallback((locationId) => {
    const key = String(locationId || '');
    const entry = lootLog.find((log) => String(log.location_id) === key);
    if (!entry?.last_loot_at) return 0;
    const elapsed = cooldownNow - parseLootTimestamp(entry.last_loot_at);
    return Math.max(0, LOOT_COOLDOWN_MS - elapsed);
  }, [cooldownNow, lootLog]);

  const formatCooldown = useCallback((ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${String(seconds).padStart(2, '0')} min`;
  }, []);

  const lootPosition = useCallback((override = null) => {
    if (override) {
      return {
        latitude: Number(override.latitude),
        longitude: Number(override.longitude),
        accuracy: override.accuracy,
        timestamp: Number(override.receivedAt || override.timestamp),
        receivedAt: Number(override.receivedAt || Date.now()),
        source: 'gps',
      };
    }
    return Array.isArray(userPos) && userPos.length === 2
      ? {
          latitude: userPos[0],
          longitude: userPos[1],
          accuracy: geolocationAccuracy,
          timestamp: Number(geolocationReceivedAt || geolocationTimestamp),
          receivedAt: Number(geolocationReceivedAt || geolocationTimestamp),
          source: 'gps',
        }
      : null;
  }, [geolocationAccuracy, geolocationReceivedAt, geolocationTimestamp, userPos]);

  const canLoot = useCallback((location) => {
    const locationId = toLocationId(location);
    if (!location || !locationId || location?.lootable === false) return false;
    return getLootEligibilityState({
      authenticated: Boolean(userId),
      position: lootPosition(),
      location,
      cooldownMs: getCooldownTime(locationId),
    }).eligible;
  }, [getCooldownTime, lootPosition, userId]);

  const setTimedLootMessage = useCallback((message) => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    setLootMessage(message);
    clearTimerRef.current = setTimeout(() => {
      setLootMessage(null);
      clearTimerRef.current = null;
    }, message?.error ? 3600 : 2600);
  }, []);

  const handleLoot = useCallback(async (location) => {
    if (claimLockRef.current || isLooting || !location) return false;
    const locationId = toLocationId(location);
    if (!locationId) {
      setTimedLootMessage({ error: 'Ungültiger Loot-Ort.', locationId });
      return false;
    }
    if (location?.lootable === false) {
      setTimedLootMessage({ error: 'Dieser Karteneintrag wird noch serverseitig geprüft.', locationId });
      return false;
    }

    let activePosition = lootPosition();
    let eligibility = getLootEligibilityState({
      authenticated: Boolean(userId),
      position: activePosition,
      location,
      cooldownMs: getCooldownTime(locationId),
    });
    const needsFreshPosition = eligibility.reason === 'gps_stale';
    if (!eligibility.eligible && !needsFreshPosition) {
      setTimedLootMessage({ error: getEligibilityMessage(eligibility), locationId });
      return false;
    }

    claimLockRef.current = true;
    setIsLooting(true);
    const requestId = createLootRequestId();
    try {
      if (needsFreshPosition) {
        if (typeof requestFreshPosition !== 'function') {
          throw new Error('Der GPS-Standort konnte nicht aktualisiert werden.');
        }
        setLootMessage({ pending: true, locationId, message: 'Standort wird einmalig aktualisiert …' });
        const refreshed = await requestFreshPosition();
        activePosition = lootPosition(refreshed);
        eligibility = getLootEligibilityState({
          authenticated: Boolean(userId),
          position: activePosition,
          location,
          cooldownMs: getCooldownTime(locationId),
        });
        if (!eligibility.eligible) {
          setTimedLootMessage({ error: getEligibilityMessage(eligibility), locationId });
          return false;
        }
      }

      const distance = distanceInMeters(
        { latitude: location.latitude ?? location.lat, longitude: location.longitude ?? location.lng },
        { latitude: activePosition.latitude, longitude: activePosition.longitude }
      );
      if (distance > LOOT_RADIUS_METERS) {
        setTimedLootMessage({ error: 'Du bist zu weit entfernt, gehe näher ran!', locationId });
        return false;
      }

      let claim;
      try {
        claim = await claimLocationLoot({
          locationId,
          spotKey: toSpotKey(location),
          latitude: activePosition.latitude,
          longitude: activePosition.longitude,
          accuracy: activePosition.accuracy,
          positionTimestamp: activePosition.receivedAt || activePosition.timestamp,
          requestId,
        });
      } catch (error) {
        if (error?.code !== 'loot_request_timeout') throw error;
        setLootMessage({
          pending: true,
          locationId,
          message: 'Die Verbindung ist langsam. Wir prüfen, ob deine Beute verbucht wurde.',
        });
        claim = await waitForLootClaimResolution(requestId);
        if (!claim) {
          throw new LootApiError('Der Loot-Status ist noch nicht eindeutig.', {
            code: 'loot_claim_status_pending',
          });
        }
      }
      const lootItem = claim?.item;
      if (!lootItem?.name) throw new Error('Der Server hat keinen Lootgegenstand zurückgegeben.');

      if (claim?.gamification && typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(claim.gamification, {
          emitXpToast: true,
          event: 'loot_find',
          awarded: Number(claim?.gamification?.awarded || 0),
        });
      }
      try {
        if (typeof playXpSound === 'function') playXpSound();
      } catch {
        // absichtlich leer
      }

      setLootLog((current) => mergeClaimIntoLootLog(current, locationId, claim));
      setTimedLootMessage({ success: true, ...lootItem, locationId });
      return true;
    } catch (error) {
      console.warn('handleLoot failed:', error);
      if (error?.code === 'cooldown_active') {
        void loadLootLog();
      }
      setTimedLootMessage({ error: getLootErrorMessage(error), locationId });
      return false;
    } finally {
      if (claimUnlockTimerRef.current) clearTimeout(claimUnlockTimerRef.current);
      claimUnlockTimerRef.current = setTimeout(() => {
        claimLockRef.current = false;
        claimUnlockTimerRef.current = null;
        setIsLooting(false);
      }, CLICK_GUARD_MS);
    }
  }, [
    applyGamificationPayload,
    geolocationAccuracy,
    geolocationReceivedAt,
    geolocationTimestamp,
    getCooldownTime,
    isLooting,
    loadLootLog,
    lootPosition,
    playXpSound,
    requestFreshPosition,
    setTimedLootMessage,
    userId,
    userPos,
  ]);

  return {
    lootMessage,
    canLoot,
    getCooldownTime,
    formatCooldown,
    handleLoot,
    isLooting,
    reloadLootLog: loadLootLog,
  };
}

export default useLooting;
